import { createSlice } from '@reduxjs/toolkit';

const messagesSlice = createSlice({
  name: 'messages',
  initialState: {
    // Messages organized by groupId
    messagesByGroup: {}, // { groupId: [messages] }
    loading: false,
    loadingMore: {}, // { groupId: boolean }
    sending: false,
    error: null,
    typingUsers: {}, // { groupId: [userId] }
    pagination: {}, // { groupId: { currentPage, hasMore, totalCount } }
  },
  reducers: {
    setMessages: (state, action) => {
      const { groupId, messages, hasMore = false, totalCount = messages.length } = action.payload;
      state.messagesByGroup[groupId] = messages;
      state.pagination[groupId] = {
        currentPage: 1,
        hasMore,
        totalCount
      };
      state.loading = false;
    },
    prependMessages: (state, action) => {
      const { groupId, messages, hasMore = false, currentPage } = action.payload;
      if (!state.messagesByGroup[groupId]) {
        state.messagesByGroup[groupId] = [];
      }
      
      // More robust duplicate prevention
      const existingIds = new Set(state.messagesByGroup[groupId].map(m => m._id));
      const newMessages = messages.filter(m => m._id && !existingIds.has(m._id));
      
      // Only update if we have new messages
      if (newMessages.length > 0) {
        state.messagesByGroup[groupId] = [...newMessages, ...state.messagesByGroup[groupId]];
      }
      
      if (state.pagination[groupId]) {
        state.pagination[groupId].currentPage = currentPage;
        state.pagination[groupId].hasMore = hasMore;
      }
      state.loadingMore[groupId] = false;
    },
    addMessage: (state, action) => {
      const { groupId, message } = action.payload;
      if (!state.messagesByGroup[groupId]) {
        state.messagesByGroup[groupId] = [];
      }
      // Check if message already exists
      const exists = state.messagesByGroup[groupId].some(m => m._id === message._id);
      if (!exists) {
        state.messagesByGroup[groupId].push(message);
        // Update total count
        if (state.pagination[groupId]) {
          state.pagination[groupId].totalCount += 1;
        }
      }
    },
    updateMessage: (state, action) => {
      const { groupId, messageId, updates } = action.payload;
      if (state.messagesByGroup[groupId]) {
        const index = state.messagesByGroup[groupId].findIndex(m => m._id === messageId);
        if (index !== -1) {
          state.messagesByGroup[groupId][index] = {
            ...state.messagesByGroup[groupId][index],
            ...updates
          };
        }
      }
    },
    deleteMessage: (state, action) => {
      const { groupId, messageId } = action.payload;
      if (state.messagesByGroup[groupId]) {
        const index = state.messagesByGroup[groupId].findIndex(m => m._id === messageId);
        if (index !== -1) {
          state.messagesByGroup[groupId][index].isDeleted = true;
          state.messagesByGroup[groupId][index].content = '[Message deleted]';
        }
      }
    },
    clearMessages: (state, action) => {
      const { groupId } = action.payload;
      if (groupId) {
        delete state.messagesByGroup[groupId];
        delete state.loadingMore[groupId];
        delete state.pagination[groupId];
      } else {
        state.messagesByGroup = {};
        state.loadingMore = {};
        state.pagination = {};
      }
    },
    setLoadingMore: (state, action) => {
      const { groupId, loading } = action.payload;
      state.loadingMore[groupId] = loading;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setSending: (state, action) => {
      state.sending = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.sending = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    addTypingUser: (state, action) => {
      const { groupId, userId } = action.payload;
      if (!state.typingUsers[groupId]) {
        state.typingUsers[groupId] = [];
      }
      if (!state.typingUsers[groupId].includes(userId)) {
        state.typingUsers[groupId].push(userId);
      }
    },
    removeTypingUser: (state, action) => {
      const { groupId, userId } = action.payload;
      if (state.typingUsers[groupId]) {
        state.typingUsers[groupId] = state.typingUsers[groupId].filter(id => id !== userId);
      }
    },
  },
});

export const {
  setMessages,
  addMessage,
  prependMessages,
  updateMessage,
  deleteMessage,
  clearMessages,
  setLoading,
  setLoadingMore,
  setSending,
  setError,
  clearError,
  addTypingUser,
  removeTypingUser,
} = messagesSlice.actions;

export default messagesSlice.reducer;
